import {
  Action,
  EntryPart,
  FiltersOperator,
  isGroupedEntry,
  isPropertyEntry,
  QueryBehaviorEntry,
  QuerySuperEntry,
} from 'common/query-builder/query-entry-section/types';
import {ChangeEvent} from 'common/components/select/types';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {generateFilterTrackingProps} from 'common/query-builder/query-block/filter-menu/util';
import {NumberOperator} from 'common/widgets/property-filter-menu/models/types';
import {QueryReason} from 'common/report/util/query-tracking/types';
import {
  propertyItemToSegmentation,
  segmentationToPropertyItem,
} from 'common/query-builder/query-block/properties-menu/util';
import {isListOfObjectsSegment} from 'common/query-builder/query-block/segment-menu/types';
import {Segmentation} from 'common/types/reports/query';
import {PropertySectionItem} from 'common/query-builder/query-block/properties-menu/item-menu-types';
import {TrackingEvent} from 'common/query-builder/query-entry-section/tracking';

import {BaseEntryRow} from '../base';
import type {EntryRow, EntryRowProps} from '../base/types';
import {applyFilterOperatorToFilter, getFilterOperatorsSections} from '../filter/utils';
import {SelectedOperator} from '../filter/constants';
import {populateListPropertyAttrs} from '../row-util';
import {SuperEntryRowEl} from './template';

/**
 * QuerySuperEntryI is a catch-all row renderer for event sections in the product (think of Funnels steps, Insights metrics, Retention)
 *
 * The idea behind this entry type is that multiple places could re-use the same workflows (event selection, filtering, etc..), it is the case
 * today that Insights is different enough that we need to rethink the structure of this component to make "metric" selection its own
 * entry type, as well as for Funnels steps
 *
 * TODO(pablo): break up all functionality from QueryEntrySection into their respective
 * selector type classes (SegmentEntry, CohortEntry, etc..)
 */
export class SuperEntryRow extends BaseEntryRow<QuerySuperEntry> implements EntryRow<QuerySuperEntry> {
  template = SuperEntryRowEl;
  property: Segmentation = null;

  constructor(props: EntryRowProps<QuerySuperEntry>) {
    super(props);

    this.property = props.entry.property
      ? propertyItemToSegmentation(props.entry.property, props.entry.property.resourceType)
      : null;
  }

  isPlaceholder() {
    if (!this.hasEventValue(this.entry) || !this.hasValidFilters()) {
      return true;
    }

    if (this.entry.property) {
      return !this.hasPropertyValue();
    }

    /** @todo [pablo] move all of behavior specific logic to a behavior entry type definition */
    if ((this.entry as QueryBehaviorEntry).entryFilter) {
      const entryFilter = new PropertyFilter((this.entry as QueryBehaviorEntry).entryFilter);
      return !entryFilter.isValid();
    }

    return false;
  }

  isIntermediatePlaceholder() {
    return this.isPlaceholder() && this.hasEventValue(this.entry);
  }

  hasValidFilters() {
    return this.entry.filters.every((filter) => new PropertyFilter(filter).isValid());
  }

  hasPropertyValue() {
    if (isListOfObjectsSegment(this.property) && Boolean(this.property.listItemGroup?.propertyName) === false) {
      return false;
    }

    return Boolean(this.property.propertyName || this.property?.customPropertyId);
  }

  hasEventValue(entry: QuerySuperEntry = this.entry) {
    // When adding a new row for filter/segment, the entry will be initialized to null. For other entry types,
    // a placeholder entry is used with `value` field null.
    if (isPropertyEntry(entry)) {
      return !!this.entry.event;
    } else if (isGroupedEntry(entry)) {
      // Grouped entries are managed by a sub- query-entry-section and always have an event.
      return true;
    } else {
      return !!entry.event?.value;
    }
  }

  get entryFilter() {
    const entryFilter = (this.entry as QueryBehaviorEntry).entryFilter;
    return entryFilter && new PropertyFilter(entryFilter);
  }

  /**
   * Funnel behaviors (did A then B) are defined with frequency filters using
   * the equals or at least numeric operators
   */
  getEntryFilterOperators() {
    const isFunnelBehavior = this.entry.behavioralFiltersOperator === FiltersOperator.Then;
    const filterOperatorsOverrides = isFunnelBehavior
      ? [{value: NumberOperator.AtLeast}, {value: NumberOperator.EqualTo}]
      : null;

    return getFilterOperatorsSections(this.entryFilter, filterOperatorsOverrides);
  }

  handleEntryFilterOperatorUpdate(ev: ChangeEvent<SelectedOperator>) {
    const [filterOperator] = ev.detail.item.value;
    const filter = applyFilterOperatorToFilter(
      filterOperator,
      new PropertyFilter((this.entry as QueryBehaviorEntry).entryFilter),
    );

    this.updateEntryFilter(filter);
  }

  handleEntryFilterValueUpdate(attribute: keyof PropertyFilter, value) {
    this.updateEntryFilter(
      new PropertyFilter((this.entry as QueryBehaviorEntry).entryFilter).setAttribute(attribute, value),
    );
  }

  updateEntryFilter(filter: PropertyFilter, trackingProps = null) {
    // focus on filter value when operator changes filter to an invalid state
    !filter.isValid() && this.section.setActiveEntry(this.index, EntryPart.EntryFilterValue);

    const entry = {
      ...this.entry,
      entryFilter: filter,
    };

    this.section.updateEntries(Action.Edit, {
      entry,
      index: this.index,
      trackingProps: trackingProps ?? generateFilterTrackingProps(filter),
    });

    this.section.trackAction(this.index, entry, Action.EditFilter);
  }

  /**
   * Handles filter updates from a nested QueryEntrySection
   */
  handleFilterSectionUpdate(
    filters: PropertyFilter[],
    filtersOperator: FiltersOperator,
    queryReason: QueryReason,
  ): void {
    const entry = {
      ...this.entry,
      filters,
      filtersOperator,
    };

    this.section.updateEntries(Action.Edit, {entry, index: this.index, queryReason});
  }

  canPerformPropertyAggregation() {
    return this.section.attr(`metrics-mode`) && this.property;
  }

  canPerformListObjectPropertySelection() {
    return this.canPerformPropertyAggregation() && isListOfObjectsSegment(this.property);
  }

  /**
   * Handles the case where a custom property entity is removed and the given entry
   * is a property aggregation type, convert it to a total count since the property
   * is no longer in a valid state
   */
  onEntryPropertyRemoved() {
    const entry = {
      ...this.entry,
      aggregationOperator: this.section.getEntryDefaultOperator(this.entry.type),
      property: null,
    };

    this.section.updateEntries(Action.Edit, {entry, index: this.index});
    this.section.dispatchTrack(TrackingEvent.ClearNumericProperty, {
      ...this.section.getEntryTrackingProps(this.index, entry),
    });
  }

  /**
   * Updates property aggregation selection on an entry
   */
  onEntryPropertySelected(property: PropertySectionItem) {
    if (!property) {
      return this.onEntryPropertyRemoved();
    }

    const segmentationProp = populateListPropertyAttrs(propertyItemToSegmentation(property, property.resourceType));
    if (isListOfObjectsSegment(segmentationProp)) {
      property = segmentationToPropertyItem(segmentationProp) as PropertySectionItem;

      this.section.setActiveEntry(this.index, EntryPart.SubValue);
    }

    const entry = {
      ...this.entry,
      property,
    };

    this.section.updateEntries(Action.Edit, {entry, index: this.index});

    this.section.dispatchTrack(TrackingEvent.SelectNumericProperty, {
      ...this.section.getEntryTrackingProps(this.index, entry),
      'Property name': entry.property.label,
      'Property data type': `type` in entry.property && entry.property.type,
    });
  }

  /**
   * Handles list of object sub property aggregations selections
   */
  handleListObjectPropertySelect(selection: Segmentation) {
    const property = {...this.property, listItemGroup: selection};

    const entry = {
      ...this.entry,
      property: segmentationToPropertyItem(property) as PropertySectionItem,
    };

    this.section.updateEntries(Action.Edit, {entry, index: this.index});
  }
}
